import {
  Button,
  Input,
  Icon,
  Avatar,
  Tag,
  Box,
  HighlightIcon,
} from '@dev-spendesk/grapes';
import classNames from 'classnames';
import React, { type MouseEventHandler } from 'react';

import { fallbackSupplierLogoSrc } from 'common/components/SupplierLogo';
import {
  hasIntegrationFileBasedExport,
  type AccountingSoftware,
} from 'modules/bookkeep/integration/status';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import {
  useAccountingSoftwareDisplayName,
  useAccountingSoftwareName,
} from 'src/core/modules/accounting-integration/apis';

import { getAccountingIntegrationDetails } from '../../../../integrations/helper';

import './AccountingBaseInput.css';

const getAccountingIntegrationIcon = (
  logoPath: string,
  brandName: string | undefined = '',
): React.ReactNode => (
  <span className="flex items-center pl-8">
    <Avatar
      variant="square"
      src={logoPath}
      fallbackSrc={fallbackSupplierLogoSrc}
      text={brandName}
      size={24}
    />
  </span>
);

export function AccountingBaseInput({
  value,
  onClick,
  className,
}: {
  value: AccountingSoftware;
  onClick?: MouseEventHandler<HTMLElement>;
  className?: string;
}) {
  const { t } = useTranslation('global');
  const { accountingSoftwareName } = useAccountingSoftwareName();

  const { logoPath, i18nSelectType: i18nType } =
    getAccountingIntegrationDetails(value);
  return (
    <Box
      data-testid="accounting-base-input"
      variant="secondary"
      className={classNames('flex flex-row justify-between', className)}
    >
      <div
        data-testid="accounting-base-input-input"
        className="flex flex-row items-center gap-16"
      >
        {hasIntegrationFileBasedExport(value) ? (
          <HighlightIcon name="table-cells" variant="forest" size={40} />
        ) : (
          getAccountingIntegrationIcon(logoPath, accountingSoftwareName)
        )}
        <div className="text-content-primary body-m ml-8">
          <span>{accountingSoftwareName}</span>
          &nbsp;
          <span className="text-content-secondary-bg-secondary">
            {t(i18nType)}
          </span>
        </div>
      </div>
      <Button
        variant="secondaryNeutral"
        text={t('bookkeep.integrations.settings.baseSelect.switch')}
        onClick={(event) => onClick && onClick(event)}
      />
    </Box>
  );
}

export function AccountingBaseToggle({
  value,
  isBeta,
}: {
  value: AccountingSoftware | 'noIntegration';
  isBeta: boolean;
}) {
  const { t } = useTranslation('global');
  const { getDisplayName } = useAccountingSoftwareDisplayName();

  if (value === 'noIntegration') {
    return (
      <Input
        className="AccountingBaseToggle"
        value=""
        type="text"
        fit="parent"
        placeholder={t('bookkeep.integrations.settings.baseSelect.placeholder')}
        rightAddon={
          <span className="AccountingBaseInput__rightIcon">
            <Icon color="currentColor" name="chevron-down" size="m" />
          </span>
        }
      />
    );
  }

  const accountingSoftwareName = getDisplayName(value);

  const { logoPath } = getAccountingIntegrationDetails(value);
  return (
    <Input
      className="AccountingBaseToggle"
      value={accountingSoftwareName}
      type="text"
      fit="parent"
      placeholder={t('bookkeep.integrations.settings.baseSelect.placeholder')}
      leftAddon={getAccountingIntegrationIcon(logoPath, accountingSoftwareName)}
      rightAddon={
        <>
          {isBeta && (
            <>
              &nbsp;
              <Tag variant="purple">{t('bookkeep.integrations.openBeta')}</Tag>
            </>
          )}

          <span className="AccountingBaseInput__rightIcon">
            <Icon color="currentColor" name="chevron-down" size="m" />
          </span>
        </>
      }
    />
  );
}
